import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, ClipboardList, Loader2, Lock, Play, RotateCcw, Send, Undo2 } from 'lucide-react';
import { agenticAPI } from '../../../../services/api';
import AttackPath from './AttackPath';
import AuthorityBox from './AuthorityBox';
import PlanFlow from './PlanFlow';
import ResetPanel from './ResetPanel';
import TeamRoster from './TeamRoster';

/**
 * The plan, and the decision the operator makes about it.
 *
 * This screen IS the gate. Before it, nothing in the engagement touches a host:
 * hypotheses sit at `planned` where the scheduler cannot see them and recon
 * refuses to dispatch. So the screen leads with the Lead's argument rather than
 * with the graph. The operator is being asked to agree with reasoning, and a
 * list of steps gives them nothing to disagree with.
 *
 * Trimming excludes a node rather than deleting it. Its hypothesis stays
 * `planned`, which lets the report say "proposed and declined" instead of
 * silently never mentioning it.
 */

const STATUS_TONE = {
  awaiting_approval: 'border-amber-300 bg-amber-50 text-amber-900',
  approved: 'border-wasabi-200 bg-wasabi-50 text-wasabi-700',
  rejected: 'border-slate-300 bg-slate-100 text-slate-600',
  superseded: 'border-slate-200 bg-slate-50 text-slate-500',
  draft: 'border-slate-300 bg-slate-50 text-slate-600',
};

const STATUS_LABEL = {
  awaiting_approval: 'Awaiting your approval',
  approved: 'Approved',
  rejected: 'Sent back',
  superseded: 'Superseded',
  draft: 'Draft',
};

/** One figure worth its own tile. Four of these replace eight stacked rows. */
function Figure({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2.5 py-2">
      <div className="font-mono text-sm font-bold text-slate-900">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

/** A figure that only appears when it is not zero, because a zero here is not
 *  news and eight rows of them is what pushed this column taller than the page
 *  had content for. */
function Line({ label, value, warn }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className={`font-mono text-xs font-semibold ${warn ? 'text-amber-700' : 'text-slate-800'}`}>
        {value}
      </span>
    </div>
  );
}

/**
 * Where the approve / send-back controls live.
 *
 * Exported so the decision dock scrolls to the decision itself. Setting the
 * tab was not enough: the operator arriving from the dock is already ON this
 * tab, so `setTab('plan')` was a no-op and the button read as broken.
 */
export const PLAN_DECISION_ID = 'plan-decision-bar';

export default function PlanView({
  pentestId, pentestName, roster, angles, planSummary, onChanged,
  //: Only supplied when the engagement has not started. See the button below.
  onStart, starting,
}) {
  const [plan, setPlan] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [excluded, setExcluded] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState('');
  const [sendingBack, setSendingBack] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [current, list] = await Promise.all([
        agenticAPI.plan(pentestId).catch((e) => {
          if (e?.response?.status === 404) return null;
          throw e;
        }),
        agenticAPI.planVersions(pentestId).catch(() => []),
      ]);
      setPlan(current);
      setVersions(list || []);
      // Start from what the operator already decided, if they decided anything.
      setExcluded(new Set((current?.graph?.excluded) || []));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the plan.');
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => { load(); }, [load]);

  // Reload when the PARENT sees the plan change.
  //
  // This screen loaded once and then sat still. Pressing Run next step from
  // here queued the job and nothing ever refreshed, so the button read as dead;
  // and "Open the plan" in the decision dock switched to a tab already showing
  // a stale "No plan yet", so that read as dead too. Both were the same
  // missing line.
  //
  // Driven off the parent's polled overview rather than a second polling loop
  // in here: the Bench already fetches this state every few seconds, and two
  // independent pollers on one screen would double the requests to disagree
  // with each other half the time.
  const seen = `${planSummary?.version ?? ''}:${planSummary?.status ?? ''}`;
  useEffect(() => {
    if (!planSummary) return;
    load();
  }, [seen, planSummary, load]);

  const nodes = useMemo(() => plan?.graph?.nodes || [], [plan]);
  const edges = useMemo(() => plan?.graph?.edges || [], [plan]);
  const selected = useMemo(
    () => nodes.find((n) => n.id === selectedId) || null,
    [nodes, selectedId],
  );
  const suggestions = plan?.graph?.scope_suggestions || [];
  const editable = plan?.status === 'awaiting_approval';

  const gates = useMemo(
    () => nodes.filter((n) => n.level_required && n.level_required !== 'confirm'),
    [nodes],
  );
  const kept = nodes.filter((n) => !excluded.has(n.id));

  const toggle = (id) => {
    if (!editable) { setSelectedId(id); return; }
    setSelectedId(id);
  };

  const exclude = (id) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const act = async (name, fn) => {
    setBusy(name);
    setError(null);
    try {
      await fn();
      await load();
      await onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : `Could not ${name}.`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading the plan...
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
        <ClipboardList className="mx-auto h-6 w-6 text-slate-400" />
        <h4 className="mt-2 text-sm font-semibold text-slate-900">No plan yet</h4>
        <p className="mx-auto mt-1 max-w-lg text-xs leading-relaxed text-slate-500">
          The Engagement Lead reads the scope, the rules of engagement and
          whatever this engagement already knows, frames what is worth an
          agent&apos;s time, and puts a plan in front of you. Nothing touches a host
          until you agree to it.
        </p>
        <p className="mx-auto mt-2 max-w-lg text-xs leading-relaxed text-slate-500">
          On a fresh engagement it knows nothing, so expect a{' '}
          <span className="font-semibold text-slate-700">recon plan</span> first:
          a short list of scanners to run against the scope. Approve it, the
          scanners run themselves, and the Lead comes back with a test plan built
          on what they found. Two approvals, not one.
        </p>
        {error && <p className="mt-3 text-xs text-red-700">{error}</p>}

        <PlanFlow />

        {/* THE ONE BUTTON, AND ONLY IN THIS STATE.
            A copy of Run next step was added here and removed three times as a
            duplicate of the banner's, and that was right every time. The rule
            was never "no button here", it was ONE ACTION IN ONE PLACE.

            On a blank engagement the whole screen is a single instruction, and
            the least findable place to put it is a small button in the corner
            of a banner. So the banner now suppresses its own action in exactly
            this state (NextStep: startsBelow) and the action lives here,
            large and central, where the eye already is. Every other state
            keeps its button in the banner. */}
        {onStart && (
          <div className="mt-6">
            <button
              type="button"
              onClick={onStart}
              disabled={starting}
              className="group mx-auto flex h-16 w-16 items-center justify-center rounded-full
                         bg-gradient-to-br from-wasabi-500 to-wasabi-700 text-white shadow-lg
                         shadow-wasabi-500/25 transition hover:scale-105 hover:shadow-xl
                         focus:outline-none focus:ring-4 focus:ring-wasabi-200
                         disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
              aria-label="Start the engagement"
            >
              {starting
                ? <Loader2 className="h-7 w-7 animate-spin" />
                : <Play className="ml-0.5 h-7 w-7 fill-current" />}
            </button>
            <p className="mt-3 text-sm font-semibold text-slate-900">
              {starting ? 'Waking the Lead...' : 'Start the engagement'}
            </p>
            <p className="mx-auto mt-1 max-w-sm text-[11px] leading-relaxed text-slate-500">
              Runs in the background and takes a minute or two. Nothing touches
              a host until you approve the plan it comes back with.
            </p>
          </div>
        )}
        <div className="mx-auto mt-6 max-w-2xl text-left">
          <ResetPanel
            pentestId={pentestId}
            pentestName={pentestName}
            onChanged={async () => { await load(); await onChanged?.(); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {error && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ONE GRID, NOT TWO.
          This was two stacked grids, and the second one held an EMPTY cell at
          1fr so the roster could sit on the right. That empty cell is what the
          operator saw as a hole in the middle of the page: the row is as tall
          as its tallest child, the roster was five members tall, and the void
          beside it was rendered at exactly that height. The attack path then
          started below both, putting the gap in the middle of the reading
          order. Now the path lives IN the main column and absorbs the height
          itself. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="min-w-0 space-y-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-wasabi-500 to-wasabi-700 text-xs font-bold text-white">
              L
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">Shinobi Lead Red Teamer</div>
              <div className="text-[11px] text-slate-500">
                plan v{plan.version} &middot; {plan.kind} plan &middot; drafted this engagement
              </div>
            </div>
            <span className={`ml-auto rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_TONE[plan.status] || STATUS_TONE.draft}`}>
              {STATUS_LABEL[plan.status] || plan.status}
            </span>
          </div>

          {/* NO BUTTON, AND THE FOURTH TIME OF ASKING. There was a Run next
              step here, a duplicate of the banner's, beside the line "This
              plan is running. Ask for another when the surface has moved on"
              - which describes nothing an operator wants to do and sat next
              to a button that did something else entirely.

              The banner above names the next action in every state. This
              strip now only says what the plan IS, because a plan that has
              already been decided is history, not a control surface. */}
          {!editable && (
            <p className="mb-3 rounded-md bg-slate-50 p-2.5 text-xs text-slate-600">
              {plan.status === 'approved'
                ? `Approved${plan.kind === 'recon' ? '. The scanners ran from this.' : '. The angles below came from this.'} Kept for the record.`
                : 'Sent back. The Lead redrafts from your note.'}
            </p>
          )}

          <p className="whitespace-pre-wrap border-l-2 border-wasabi-300 pl-3 text-[13px] leading-relaxed text-slate-700">
            {plan.brief || 'The Lead drafted this plan without an argument, which is itself worth asking about.'}
          </p>

          {plan.operator_note && (
            <p className="mt-3 rounded-md bg-slate-50 p-2.5 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">You said:</span> {plan.operator_note}
            </p>
          )}
        </div>

      <AuthorityBox pentestId={pentestId} nodes={nodes} onChanged={onChanged} />

      {plan.dropped_node_count > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {plan.dropped_node_count} step{plan.dropped_node_count === 1 ? '' : 's'} in this
            plan cited ids that do not exist on this engagement and were removed before you
            saw it. A rising number here is the earliest sign the model is drifting.
          </span>
        </div>
      )}

      {/* the path */}
      <div>
        <div className="mb-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          {[
            ['#16a34a', 'An agent on it now'],
            ['#0891b2', 'Confirmed'],
            ['#7c3aed', 'Refuted'],
            ['#e11d48', 'Failed'],
            ['#d97706', 'Needs a raise'],
          ].map(([colour, label]) => (
            <span key={label} className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colour }} /> {label}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm border border-dashed border-slate-300 bg-slate-50" /> Excluded by you
          </span>
          <span className="ml-auto">Click a step to read it.</span>
        </div>
        <AttackPath
          nodes={nodes}
          edges={edges}
          excluded={excluded}
          selectedId={selectedId}
          onSelect={toggle}
          angles={angles}
          roster={roster}
          // What the recon nodes actually did, keyed by node id in AttackPath.
          // Lives beside the nodes rather than on them (see recon.py's own
          // comment on `entries`): the graph is the plan the operator approved,
          // and dispatch outcomes are appended data, not a rewrite of it.
          recon={plan?.graph?.recon}
        />
      </div>

      {/* the selected step */}
      {selected && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {selected.column}
                {selected.tool ? ` · ${selected.tool}` : ''}
                {selected.level_required && selected.level_required !== 'confirm'
                  ? ` · needs ${selected.level_required}`
                  : ''}
              </div>
              <h4 className="mt-0.5 text-sm font-semibold text-slate-900">{selected.label}</h4>
              {selected.detail && (
                <p className="mt-1 text-xs leading-relaxed text-slate-600">{selected.detail}</p>
              )}
            </div>
            {editable && (
              <button
                type="button"
                onClick={() => exclude(selected.id)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold ${
                  excluded.has(selected.id)
                    ? 'border-wasabi-300 text-wasabi-700 hover:bg-wasabi-50'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                {excluded.has(selected.id)
                  ? <><Undo2 className="h-3.5 w-3.5" /> Put it back</>
                  : <><Lock className="h-3.5 w-3.5" /> Leave this out</>}
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {selected.ref && (
              <span className="rounded border border-wasabi-200 bg-wasabi-50 px-1.5 py-0.5 font-mono text-[10px] text-wasabi-700">
                {selected.ref}
              </span>
            )}
            {(selected.based_on || []).map((citation) => (
              <span key={citation}
                    className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">
                {citation}
              </span>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Every citation was checked against this engagement&apos;s rows before the plan
            reached you.
          </p>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-slate-900">
            Targets the Lead thinks are worth adding
          </h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Suggestions only. Nothing here is in scope, and nothing will touch it unless
            you add it on Pre-Engagement.
          </p>
          <ul className="mt-2 space-y-1.5">
            {suggestions.map((s) => (
              <li key={s.value} className="rounded-md bg-slate-50 p-2 text-xs">
                <span className="font-mono font-semibold text-slate-800">{s.value}</span>
                <span className="text-slate-600"> &mdash; {s.why}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {versions.length > 1 && (
        <div className="text-[11px] text-slate-500">
          {versions.length} versions on this engagement:{' '}
          {versions.map((v) => `v${v.version} ${v.kind} (${v.status})`).join(' · ')}
        </div>
      )}
        </div>

        {/* THE RAIL.
            The estimates were eight stacked rows, six of which read zero on a
            real plan, and a stack of zeroes is what made this column tall
            enough to leave a hole beside it. They are a strip now: the numbers
            worth a whole row are the ones that are not zero. */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              What you are agreeing to
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Figure label="steps" value={`${kept.length}/${nodes.length}`} />
              <Figure label="angles" value={plan.estimates?.hypotheses ?? 0} />
              <Figure label="recon" value={plan.estimates?.recon_steps ?? 0} />
              <Figure label="requests" value={(plan.estimates?.requests ?? 0).toLocaleString()} />
            </div>
            {(gates.length > 0 || plan.dropped_node_count > 0
              || (plan.estimates?.tokens ?? 0) > 0) && (
              <div className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                {(plan.estimates?.tokens ?? 0) > 0 && (
                  <Line label="Estimated tokens"
                        value={(plan.estimates?.tokens ?? 0).toLocaleString()} />
                )}
                {gates.length > 0 && (
                  <Line label="Needs authority above the floor" value={gates.length} warn />
                )}
                {plan.dropped_node_count > 0 && (
                  <Line label="Discarded by the validator"
                        value={plan.dropped_node_count} warn />
                )}
              </div>
            )}
          </div>

          {/* Approving this plan is what causes pentesters to exist, so "who is
              on this" belongs beside the decision rather than behind a tab.
              Capped and scrolling: a fifth member used to stretch the page. */}
          <div>
            <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              The team on this engagement
            </div>
            <div className="max-h-[26rem] overflow-y-auto pr-0.5">
              <TeamRoster roster={roster} pentestId={pentestId} onChanged={onChanged} />
            </div>
          </div>
        </aside>
      </div>

      {/* THE DECISION. It was already sticky and still got missed: a thin
          white strip with a 12px button reads as more page, not as the thing
          the whole screen exists for, so the operator scrolled straight past
          it. Weight was the problem, not position. */}
      {editable && (
        <div className="sticky bottom-0 -mx-1 border-t-2 border-wasabi-200 bg-white/95 px-1 py-4 shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.25)] backdrop-blur">
          {sendingBack && (
            <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <label htmlFor="plan-note" className="text-xs font-semibold text-slate-700">
                What should it do differently?
              </label>
              <textarea
                id="plan-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Too broad. Drop the recon on the beta host and start from the .env exposure."
                className="mt-1 w-full rounded-md border border-slate-300 p-2 text-xs focus:border-wasabi-500 focus:outline-none"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                The Lead reads this on its next turn and drafts a new version. Sending a
                plan back without a reason gets you the same plan again.
              </p>
            </div>
          )}

          {/* Anchored so the decision dock can scroll the operator to the
              DECISION rather than to the tab. Its "Open the plan" button did
              nothing at all when they were already on this tab, which is the
              usual case: the dock is what sent them here. */}
          <div
            id={PLAN_DECISION_ID}
            className="flex scroll-mt-24 flex-wrap items-center justify-between gap-3"
          >
            <div className="text-xs text-slate-600">
              <span className="font-semibold text-slate-900">
                {kept.length} of {nodes.length}
              </span>{' '}
              steps selected
              {gates.length > 0 && (
                <span className="text-amber-700"> &middot; {gates.length} need a raise</span>
              )}
              <div className="text-[11px] text-slate-400">
                Nothing has touched a host yet.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {excluded.size > 0 && (
                <button
                  type="button"
                  onClick={() => setExcluded(new Set())}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-2 text-xs font-medium text-slate-500 hover:text-slate-800"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Reset trim
                </button>
              )}
              {sendingBack ? (
                <>
                  <button
                    type="button"
                    onClick={() => { setSendingBack(false); setNote(''); }}
                    className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={!note.trim() || !!busy}
                    onClick={() => act('send the plan back', async () => {
                      await agenticAPI.rejectPlan(pentestId, plan.version, note.trim());
                      setSendingBack(false);
                      setNote('');
                    })}
                    className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                  >
                    {busy === 'send the plan back'
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Send className="h-3.5 w-3.5" />}
                    Send it back
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setSendingBack(true)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Send back with a note
                  </button>
                  <button
                    type="button"
                    disabled={!!busy || kept.length === 0}
                    onClick={() => act('approve the plan', () =>
                      agenticAPI.approvePlan(pentestId, plan.version, {
                        exclude: [...excluded],
                        note: note.trim() || null,
                      }))}
                    className="inline-flex items-center gap-2 rounded-lg bg-wasabi-500 px-6 py-3 text-sm font-semibold text-white shadow-md shadow-wasabi-500/25 transition hover:bg-wasabi-600 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-wasabi-200 disabled:opacity-50"
                  >
                    {busy === 'approve the plan'
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Check className="h-4 w-4" />}
                    Approve and start
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {plan.status === 'approved' && (
        <div className="rounded-md border border-wasabi-200 bg-wasabi-50 p-3 text-xs text-wasabi-800">
          Approved. {plan.kind === 'recon'
            ? 'Recon runs itself from here, with no agent and no model turn. The Lead drafts the test plan once it lands.'
            : 'The angles you kept are approved, and agents pick them up as slots free.'}
        </div>
      )}

      {plan.status === 'rejected' && (
        <div className="rounded-md border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
          Sent back. Run the next step and the Lead will draft a new version from
          your note.
        </div>
      )}

      <ResetPanel
        pentestId={pentestId}
        pentestName={pentestName}
        onChanged={async () => { await load(); await onChanged?.(); }}
      />
    </div>
  );
}
